/**
 * StockItemDetailsDialog - Ficha somente leitura de Rastreador, Chip e Kit.
 *
 * A ficha compartilha um único leiaute para os três produtos e já reserva abas
 * futuras. O histórico é consultado por RPC e inclui movimentações via kit para
 * Rastreador e Chip.
 *
 * RELEASE: 2026-10-14 v1.2.0 - Selo "Cancelamento solicitado" no chip com cancelamento aberto.
 * RELEASE: 2026-10-11 v1.1.0 - Aba Homologações (somente rastreador), reutilizando StockHomologationsTable.
 * RELEASE: 2026-10-08 v1.0.0 - Detalhes e movimentações individuais do produto.
 */
import { StockComponentStatus } from '@/components/stock/StockComponentStatus';
import { useEffect, useState } from 'react';
import { Box, Cpu, CreditCard, Loader2, MapPin, Package, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FormSection } from '@/components/common/FormSection';
import { CANCEL_STATUS, useChipOpenCancellation } from '@/hooks/useStockChipCancellations';
import { StockMovementsTable } from '@/components/stock/StockMovementsTable';
import { StockPlaceLabel } from '@/components/stock/StockPlaceLabel';
import { StockHomologationsTable } from '@/components/stock/StockHomologationsTable';
import { useStockHomologationsList } from '@/hooks/useStockHomologations';
import { useIsMobile } from '@/hooks/use-mobile';
import type { StockItemKind, StockItemRow } from '@/hooks/useStockItems';
import { stockStatusTone } from '@/hooks/useStockItems';
import { useStockMovementsList, type StockMovementsFilters } from '@/hooks/useStockMovements';
import { dataPlanCarrierLabel, formatPackageMb } from '@/hooks/useStockRegistries';
import { cn } from '@/lib/utils';
import { formatStockDateTime, stockStatusLabel } from '@/lib/stock-presentation';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';

const PRODUCT = {
  tracker: { label: 'Rastreador', icon: Cpu },
  chip: { label: 'Chip', icon: CreditCard },
  kit: { label: 'Kit', icon: Package },
} as const;

interface Props {
  kind: StockItemKind;
  item: StockItemRow | null;
  onOpenChange: (open: boolean) => void;
}

/** Campo de leitura com rótulo discreto. */
function Info({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className={cn('break-words text-sm font-medium', mono && 'tabular-nums')}>{value ?? '—'}</div>
    </div>
  );
}

/** Conteúdo interno separado para só consultar o histórico quando houver produto aberto. */
function StockItemDetailsContent({ kind, item, onOpenChange }: { kind: StockItemKind; item: StockItemRow; onOpenChange: (open: boolean) => void }) {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('details');
  const [filters, setFilters] = useState<StockMovementsFilters>({
    search: '', itemType: kind, itemId: item.id, destinationType: 'all', dateFrom: null, dateTo: null, page: 1, pageSize: 10,
  });
  const { data, isLoading, isFetching, refetch } = useStockMovementsList(filters);
  // 2026-10-11: histórico de homologações do rastreador (consulta só com a aba aberta)
  const homologations = useStockHomologationsList({
    search: '', type: 'all', result: 'all', dateFrom: null, dateTo: null, trackerId: item.id,
    sortField: 'created_at', sortDirection: 'desc', page: 1, pageSize: 50,
  }, kind === 'tracker' && tab === 'homologations');
  const cfg = PRODUCT[kind];
  // 2026-10-14: cancelamento aberto do chip (pré-solicitação ou solicitado)
  const { data: openCancel } = useChipOpenCancellation(kind === 'chip' ? item.iccid : null, kind === 'chip');
  const Icon = cfg.icon;

  // Reinicia aba e paginação ao abrir outro produto sem remontar a página.
  useEffect(() => {
    setTab('details');
    setFilters({ search: '', itemType: kind, itemId: item.id, destinationType: 'all', dateFrom: null, dateTo: null, page: 1, pageSize: 10 });
  }, [item.id, kind]);

  const title = kind === 'tracker' ? item.imei : kind === 'chip' ? item.iccid : item.code;
  const status = stockStatusLabel(kind, item.status, !!item.dissolved_at);
  const model = item.model_name ? `${item.manufacturer_name ? `${item.manufacturer_name} · ` : ''}${item.model_name}` : '—';

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className={cn('flex flex-col gap-0 overflow-hidden p-0', isMobile ? 'h-[100dvh] w-screen max-w-full rounded-none' : 'max-h-[90vh] max-w-5xl')}>
        <DialogHeader className="shrink-0 border-b p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2.5"><Icon className="h-5 w-5 text-primary" /></div>
            <div className="min-w-0 text-left">
              <DialogTitle className="truncate text-lg">{cfg.label} {title ?? ''}</DialogTitle>
              <DialogDescription>Detalhes e histórico individual do produto.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 border-b px-6">
            <TabsList className="h-auto justify-start gap-6 rounded-none bg-transparent p-0">
              <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent px-0 py-3 shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">Detalhes</TabsTrigger>
              <TabsTrigger value="movements" className="rounded-none border-b-2 border-transparent px-0 py-3 shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">Movimentações</TabsTrigger>
              {kind === 'tracker' && (
                <TabsTrigger value="homologations" className="rounded-none border-b-2 border-transparent px-0 py-3 shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">Homologações</TabsTrigger>
              )}
            </TabsList>
          </div>

          <TabsContent value="details" className="mt-0 min-h-0 flex-1">
            <ScrollArea className="h-full max-h-[calc(90vh-142px)]">
              <div className="space-y-6 p-6">
                <FormSection icon={Box} title="Identificação">
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {kind === 'tracker' && <>
                      <Info label="IMEI" value={item.imei} mono />
                      <Info label="ESN" value={item.esn || '—'} mono />
                      <Info label="Modelo" value={model} />
                      <Info label="Fornecedor" value={item.supplier_name || '—'} />
                      <Info label="Kit vinculado" value={item.kit_code || 'Livre'} />
                    </>}
                    {kind === 'chip' && <>
                      <Info label="ICCID" value={item.iccid} mono />
                      <Info label="Linha" value={item.line_number || '—'} mono />
                      <Info label="IMSI" value={item.imsi || '—'} mono />
                      <Info label="Plano de dados" value={item.data_plan_name || '—'} />
                      <Info label="Pacote" value={item.data_plan_id ? formatPackageMb(item.data_plan_package_mb) : '—'} />
                      <Info label="Fornecedor" value={item.data_plan_supplier_name || '—'} />
                      <Info label="Operadoras" value={(item.data_plan_carriers?.length ?? 0) > 0 ? item.data_plan_carriers?.map(dataPlanCarrierLabel).join(', ') : '—'} />
                      <Info label="Kit vinculado" value={item.kit_code || 'Livre'} />
                      {openCancel && (
                        <Info label="Cancelamento" value={(
                          <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase">
                            <StatusDot tone={CANCEL_STATUS[openCancel.status].tone} />
                            {openCancel.protocol_number ? `Solicitado — protocolo ${openCancel.protocol_number}` : 'Cancelamento em pré-solicitação'}
                          </Badge>
                        )} />
                      )}
                    </>}
                    {kind === 'kit' && <>
                      <Info label="Código" value={item.code} />
                      <Info label="Montado em" value={formatStockDateTime(item.formed_at)} mono />
                      {/* Situação individual de cada componente ao lado do IMEI/ICCID */}
                      <Info label="Rastreador" value={<span className="inline-flex flex-wrap items-center gap-2">{item.imei || '—'}<StockComponentStatus kind="tracker" status={item.tracker_status} className="font-sans text-xs" /></span>} mono />
                      <Info label="Modelo" value={item.model_name || '—'} />
                      <Info label="Chip" value={<span className="inline-flex flex-wrap items-center gap-2">{item.iccid || '—'}<StockComponentStatus kind="chip" status={item.chip_status} className="font-sans text-xs" /></span>} mono />
                      <Info label="Plano de dados" value={item.data_plan_name || '—'} />
                    </>}
                    <Info label="Situação" value={(
                      <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase">
                        <StatusDot tone={stockStatusTone(item.dissolved_at ? 'dissolved' : item.status)} />{status}
                      </Badge>
                    )} />
                    {kind !== 'kit' && <Info label="Cadastro" value={item.is_active ? 'Ativo' : 'Inativo'} />}
                  </div>
                </FormSection>

                <Separator />

                <FormSection icon={MapPin} title="Local atual">
                  <StockPlaceLabel place={item.place ?? null} />
                </FormSection>

                <Separator />

                <FormSection icon={Box} title="Observações">
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{item.notes || 'Nenhuma observação.'}</p>
                </FormSection>
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="movements" className="mt-0 flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b px-6 py-3">
              <p className="text-sm text-muted-foreground">{data?.total ?? 0} movimentação(ões)</p>
              <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => refetch()} disabled={isFetching} title="Atualizar movimentações" aria-label="Atualizar movimentações">
                <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
              <StockMovementsTable items={data?.items ?? []} isLoading={isLoading} compact />
            </div>
            <div className="shrink-0 border-t px-6 py-3">
              <PaginationFooter totalItems={data?.total ?? 0} currentPage={filters.page} pageSize={filters.pageSize}
                onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
                onPageSizeChange={(pageSize) => setFilters((current) => ({ ...current, pageSize, page: 1 }))}
                pageSizeOptions={[10, 25, 50]} />
            </div>
          </TabsContent>
          {kind === 'tracker' && (
            <TabsContent value="homologations" className="mt-0 min-h-0 flex-1 overflow-auto px-6 py-4">
              <StockHomologationsTable items={homologations.data?.items ?? []} isLoading={homologations.isLoading} hideTracker />
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/** Monta a ficha apenas quando uma linha estiver selecionada. */
export function StockItemDetailsDialog({ kind, item, onOpenChange }: Props) {
  if (!item) return null;
  return <StockItemDetailsContent kind={kind} item={item} onOpenChange={onOpenChange} />;
}